import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import DataTable from "../../common/dataTable";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import { useCapaAccess, canDeleteCapaRecord, canUpdateCapaRecord } from "./capaAccess";
import { fetchCapaList, removeCapa } from "./capaService";
import { INITIAL_CAPA_STATUS } from "./capaWorkflow";
import CapaListFilterDrawer from "./CapaListFilterDrawer";
import {
  CAPA_LIST_FILTER_INITIAL,
  countActiveFilters,
  filterCapaRows,
} from "./capaListFilters";
import {
  getCapaRiskBadgeStyle,
  getCapaStatusBadgeStyle,
} from "./capaConstants";
import HoverPreview from "../../common/HoverPreview/HoverPreview";
import { getAvatarColor, getInitials, stripHtml } from "../../../utils/avatarUtils";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import { useLoader } from "../../../context/LoaderContext";
import "./CapaPages.scss";

const CapaList = () => {
  const navigate = useNavigate();
  const { userData, canViewCapa, canAddCapa, isSystemAdmin } = useCapaAccess();
  const { showLoader, hideLoader } = useLoader();
  const [rows, setRows] = useState([]);
  const lazyBindTimerRef = useRef(null);
  const lazyBindSessionRef = useRef(0);
  const activeLoadSessionRef = useRef(0);
  const ownerFallbackName = [userData?.FirstName, userData?.LastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(CAPA_LIST_FILTER_INITIAL);
  const [draftFilters, setDraftFilters] = useState(CAPA_LIST_FILTER_INITIAL);

  const clearLazyBindTimer = useCallback(() => {
    if (lazyBindTimerRef.current) {
      clearTimeout(lazyBindTimerRef.current);
      lazyBindTimerRef.current = null;
    }
  }, []);

  const progressivelyBindRows = useCallback(
    (list, { stopLoaderAfterFirstChunk = true } = {}) => {
      clearLazyBindTimer();
      lazyBindSessionRef.current += 1;
      const session = lazyBindSessionRef.current;
      const safeList = Array.isArray(list) ? list : [];
      const LAZY_BIND_THRESHOLD = 1500;
      const firstChunkSize = 10;
      const chunkSize = 1000;
      let cursor = firstChunkSize;

      // For common list sizes, bind everything immediately for fastest UX.
      if (safeList.length <= LAZY_BIND_THRESHOLD) {
        setRows(safeList);
        if (stopLoaderAfterFirstChunk) {
          hideLoader();
        }
        return;
      }

      setRows(safeList.slice(0, firstChunkSize));
      if (stopLoaderAfterFirstChunk) {
        hideLoader();
      }

      const pump = () => {
        if (lazyBindSessionRef.current !== session) return;
        if (cursor >= safeList.length) return;
        const next = safeList.slice(0, Math.min(cursor + chunkSize, safeList.length));
        setRows(next);
        cursor += chunkSize;
        if (cursor < safeList.length) {
          lazyBindTimerRef.current = setTimeout(pump, 16);
        }
      };

      if (safeList.length > firstChunkSize) {
        lazyBindTimerRef.current = setTimeout(pump, 16);
      }
    },
    [clearLazyBindTimer, hideLoader],
  );

  const loadRows = useCallback(async () => {
    activeLoadSessionRef.current += 1;
    const session = activeLoadSessionRef.current;
    showLoader("Loading CAPA list...");
    try {
      const list = await fetchCapaList(userData);
      progressivelyBindRows(list, { stopLoaderAfterFirstChunk: true });
    } finally {
      // Avoid stale requests hiding/showing loader repeatedly.
      if (activeLoadSessionRef.current === session) {
        hideLoader();
      }
    }
  }, [hideLoader, progressivelyBindRows, showLoader, userData]);

  useEffect(() => {
    let cancelled = false;
    activeLoadSessionRef.current += 1;
    const session = activeLoadSessionRef.current;
    const orgId = userData?.OrganizationId;
    const staffId = userData?.StaffId;
    if (!orgId || !staffId) return undefined;
    (async () => {
      showLoader("Loading CAPA list...");
      try {
        const list = await fetchCapaList({
          OrganizationId: orgId,
          StaffId: staffId,
        });
        if (!cancelled) {
          progressivelyBindRows(list, { stopLoaderAfterFirstChunk: true });
        }
      } finally {
        if (!cancelled && activeLoadSessionRef.current === session) {
          hideLoader();
        }
      }
    })();
    return () => {
      cancelled = true;
      clearLazyBindTimer();
      lazyBindSessionRef.current += 1;
      activeLoadSessionRef.current += 1;
      hideLoader();
    };
    // Loader callbacks can change identity between renders; do not refetch on that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.OrganizationId, userData?.StaffId]);

  const uniqueOwners = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const disp =
        r.OwnerName === "You (current user)"
          ? ownerFallbackName || userData?.Name || r.OwnerName
          : r.OwnerName || "—";
      set.add(disp);
    });
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [rows, ownerFallbackName, userData?.Name]);

  const filteredRows = useMemo(
    () => filterCapaRows(rows, appliedFilters, ownerFallbackName, userData),
    [rows, appliedFilters, ownerFallbackName, userData],
  );

  const activeFilterCount = useMemo(
    () => countActiveFilters(appliedFilters),
    [appliedFilters],
  );

  const openFilterPanel = useCallback(() => {
    setDraftFilters(appliedFilters);
    setFilterOpen(true);
  }, [appliedFilters]);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters(draftFilters);
    setFilterOpen(false);
  }, [draftFilters]);

  const handleClearFilters = useCallback(() => {
    setDraftFilters(CAPA_LIST_FILTER_INITIAL);
    setAppliedFilters(CAPA_LIST_FILTER_INITIAL);
  }, []);

  const handleRefreshData = useCallback(async () => {
    await loadRows();
  }, [loadRows]);

  const handleRemoveCapa = useCallback(
    async (row) => {
      if (
        !row?.CapaId ||
        !canDeleteCapaRecord({
          capaRow: row,
          userData,
          isSystemAdmin,
        })
      ) {
        return;
      }
      const authRes = await openAuthModal({
        showDescription: true,
        requireDescription: true,
      });
      if (authRes?.Success !== 1) return;
      const reason = String(authRes?.Description || "").trim();
      if (!reason) return;
      showLoader("Removing CAPA...");
      try {
        await removeCapa(row.CapaId, { reason }, userData);
        await loadRows();
      } finally {
        hideLoader();
      }
    },
    [hideLoader, isSystemAdmin, loadRows, showLoader, userData],
  );

  const handleExportToExcel = useCallback(async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("CAPA List");
    const headers = [
      "CAPA ID",
      "Title",
      "Description",
      "Context",
      "Module",
      "Source",
      "Risk level",
      "Status",
      "Owner",
      "Created",
    ];
    const headerRow = ws.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF4472C4" },
      };
    });

    filteredRows.forEach((row) => {
      const owner =
        row.OwnerName === "You (current user)"
          ? ownerFallbackName || userData?.Name || row.OwnerName
          : row.OwnerName || "—";
      ws.addRow([
        row.CapaNo || "",
        stripHtml(String(row.Title || "")),
        stripHtml(String(row.Description || "")),
        row.ContextLabel || "",
        row.ModuleLabel || "",
        row.SourceType || "",
        row.Risk || "",
        row.Status || "",
        owner,
        row.CreatedAt || "",
      ]);
    });

    ws.columns = headers.map((h) => ({
      header: h,
      key: h,
      width: Math.max(14, h.length + 2),
    }));

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `CAPA_List_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [filteredRows, ownerFallbackName, userData?.Name]);

  const columns = useMemo(
    () => [
      { label: "CAPA ID", accessor: "CapaNo" },
      {
        label: "Title",
        accessor: "Title",
        render: (row) => {
          const raw = String(row.Title || "");
          const plain = stripHtml(raw);
          if (!plain.trim()) {
            return <span className="text-muted">—</span>;
          }
          return (
            <HoverPreview
              content={raw}
              plainTextForPreview={plain}
              isHtml={/<[^>]+>/.test(raw)}
              anchorShowHtml={false}
              previewLength={99999}
              appendEllipsis={false}
              maxWidth={480}
              maxHeight={240}
            />
          );
        },
      },
      {
        label: "Description",
        accessor: "Description",
        render: (row) => {
          const raw = String(row.Description || "");
          const plain = stripHtml(raw);
          if (!plain.trim()) {
            return <span className="text-muted">—</span>;
          }
          return (
            <HoverPreview
              content={raw}
              plainTextForPreview={plain}
              isHtml={/<[^>]+>/.test(raw)}
              anchorShowHtml={false}
              previewLength={99999}
              appendEllipsis={false}
              maxWidth={480}
              maxHeight={280}
            />
          );
        },
      },
      { label: "Context", accessor: "ContextLabel" },
      { label: "Module", accessor: "ModuleLabel" },
      { label: "Source", accessor: "SourceType" },
      {
        label: "Risk level",
        accessor: "Risk",
        render: (row) => {
          const label = String(row.Risk || "Medium").trim() || "Medium";
          const style = getCapaRiskBadgeStyle(label);
          return (
            <span
              className="badge rounded-pill"
              style={{
                ...style,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.68rem",
                fontWeight: 600,
                lineHeight: 1.1,
                padding: "0.18em 0.5em",
              }}
            >
              {label}
            </span>
          );
        },
      },
      {
        label: "Status",
        accessor: "Status",
        render: (row) => {
          const label = String(row.Status || "—").trim() || "—";
          const style = getCapaStatusBadgeStyle(row.Status);
          return (
            <span
              className="badge rounded-pill"
              style={{
                ...style,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.68rem",
                fontWeight: 600,
                lineHeight: 1.1,
                padding: "0.18em 0.5em",
                maxWidth: "100%",
                whiteSpace: "normal",
                textAlign: "center",
              }}
            >
              {label}
            </span>
          );
        },
      },
      {
        label: "Owner",
        accessor: "OwnerName",
        align: "center",
        render: (row) => {
          const name =
            row.OwnerName === "You (current user)"
              ? ownerFallbackName || userData?.Name || row.OwnerName
              : row.OwnerName || "—";
          return (
            <div className="capa-list-owner-only">
              <div
                className="capa-list-owner-avatar"
                style={{ backgroundColor: getAvatarColor(name) }}
                title={name}
              >
                {getInitials(name)}
              </div>
            </div>
          );
        },
      },
      { label: "Created", accessor: "CreatedAt", type: "date" },
    ],
    [ownerFallbackName, userData?.Name],
  );

  const actions = [
    {
      label: "View",
      className: "btn-primary btn-sm",
      onClick: (row) => navigate(`/capa/${row.CapaId}`, { state: { capaRow: row } }),
    },
    {
      label: "Edit",
      className: "btn-outline-secondary btn-sm",
      onClick: (row) => navigate(`/capa/${row.CapaId}/edit`),
      visible: (row) =>
        String(row?.Status || "").trim().toLowerCase() ===
          String(INITIAL_CAPA_STATUS).toLowerCase() &&
        canUpdateCapaRecord({
          capaRow: row,
          userData,
          isSystemAdmin,
        }),
    },
    {
      label: "Remove",
      className: "btn-outline-danger btn-sm",
      onClick: handleRemoveCapa,
      visible: (row) =>
        canDeleteCapaRecord({
          capaRow: row,
          userData,
          isSystemAdmin,
        }),
    },
  ];

  if (!userData) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" />
      </div>
    );
  }
  if (!canViewCapa) return <Navigate to="/notification-center" replace />;

  return (
    <div className="capa-page">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "CAPA list" },
        ]}
        toolbarExtras={
          <div className="d-inline-block position-relative">
            <button
              type="button"
              className="actions-trigger capa-list-filter-trigger"
              onClick={openFilterPanel}
            >
              <i className="fas fa-filter me-1" aria-hidden />
              Filter
            </button>
            {activeFilterCount > 0 ? (
              <span
                role="button"
                tabIndex={0}
                className="capa-list-filter-trigger__badge position-absolute top-0 end-0"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClearFilters();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClearFilters();
                  }
                }}
                title="Clear filters"
                aria-label="Clear filters"
                style={{
                  zIndex: 10,
                  cursor: "pointer",
                  pointerEvents: "auto",
                  top: "-0.35rem",
                  right: "-0.35rem",
                }}
              >
                {activeFilterCount} ×
              </span>
            ) : null}
          </div>
        }
        actions={[
          { icon: "fas fa-sync-alt", label: "Refresh Data", onClick: handleRefreshData },
          ...(canAddCapa
            ? [
                {
                  icon: "fas fa-plus",
                  label: "Create CAPA",
                  onClick: () => navigate("/capa/new"),
                },
              ]
            : []),
          { icon: "fas fa-file-excel", label: "Export to Excel", onClick: handleExportToExcel },
        ]}
      />

      <CapaListFilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        draftFilters={draftFilters}
        setDraftFilters={setDraftFilters}
        onApply={handleApplyFilters}
        onClearFilters={handleClearFilters}
        uniqueOwners={uniqueOwners}
        userData={userData}
      />

      <DataTable
        data={filteredRows}
        columns={columns}
        actions={actions}
        sortable={[
          "CapaNo",
          "Title",
          "Risk",
          "Status",
          "CreatedAt",
          "OwnerName",
        ]}
        showToggle={true}
      />
    </div>
  );
};

export default CapaList;
